/* eslint-disable react-hooks/exhaustive-deps */
// TODO: Fix ESLint errors and improve type safety
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUiModeStore } from '@/features/ui-mode/store/ui-mode-store';
import {
  useChargePointsStore,
  type ChargePointsState,
} from '../../store/charge-points-store';
import { chargePointsApi } from '../../api/charge-points-api';
import type { ChargePointWrapper } from '../../types/charge-point.types';
import { AvailabilitySection } from './AvailabilitySection';
import { AvailabilityHero } from './AvailabilityHero';
import { AddressSection } from './AddressSection';
import { ChargePointsList } from './ChargePointsList';
import { PricingSection } from './PricingSection';
import { FeesSection } from './FeesSection';
import { ActionButtons } from './ActionButtons';
import { PowerGroupedSummary } from './PowerGroupedSummary';
import { OverlapNavigator } from './OverlapNavigator';
import { ChargePointSkeleton } from './ChargePointSkeleton';
import { OperatorNote } from './OperatorNote';

interface ChargePointInfoPanelProps {
  onClose: () => void;
}

/**
 * Main info panel for displaying charge point details
 * Used in both mobile (bottom sheet) and desktop (side panel) layouts
 */
export function ChargePointInfoPanel({ onClose }: ChargePointInfoPanelProps) {
  const { t } = useTranslation();
  const uiMode = useUiModeStore((s) => s.uiMode);
  const selectedWrapper = useChargePointsStore(
    (s: ChargePointsState) => s.selectedChargePoint
  );
  const selectedGroupMembers = useChargePointsStore(
    (s: ChargePointsState) => s.selectedGroupMembers
  );
  const setSelectedGroupMembers = useChargePointsStore(
    (s: ChargePointsState) => s.setSelectedGroupMembers
  );
  const getCachedGroupMembers = useChargePointsStore(
    (s: ChargePointsState) => s.getCachedGroupMembers
  );
  const selectChargePoint = useChargePointsStore(
    (s: ChargePointsState) => s.selectChargePoint
  );
  const parentGroup = useChargePointsStore(
    (s: ChargePointsState) => s.parentGroup
  );
  const setParentGroup = useChargePointsStore(
    (s: ChargePointsState) => s.setParentGroup
  );
  const isLoadingGroup = useChargePointsStore(
    (s: ChargePointsState) => s.isLoadingGroup
  );
  const isLoadingDetails = useChargePointsStore(
    (s: ChargePointsState) => s.isLoadingDetails
  );
  const setLoadingGroup = useChargePointsStore(
    (s: ChargePointsState) => s.setLoadingGroup
  );
  const setLoadingDetails = useChargePointsStore(
    (s: ChargePointsState) => s.setLoadingDetails
  );
  const urlChargePointId = useChargePointsStore(
    (s: ChargePointsState) => s.urlChargePointId
  );
  const urlChargePointSiteId = useChargePointsStore(
    (s: ChargePointsState) => s.urlChargePointSiteId
  );

  // Handle ESC key to close panel
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Handle deeplink with both cpSiteId and chargePointId - fetch parent site
  useEffect(() => {
    // Only run if we have both URL parameters and no parentGroup yet
    if (urlChargePointId && urlChargePointSiteId && !parentGroup) {
      // Fetch the parent site and set it as parentGroup
      chargePointsApi
        .getChargePointGroupMembers(urlChargePointSiteId)
        .then((members) => {
          // Cache the members
          setSelectedGroupMembers(members, urlChargePointSiteId);

          // Create a wrapper for the parent site
          // We need to construct the site info from the first member
          const firstMember = members[0];
          if (firstMember) {
            const siteWrapper = {
              id: `group-${urlChargePointSiteId}`,
              type: 'chargePointGroup' as const,
              coordinate: firstMember.coordinate,
              pin: firstMember.pin,
              chargePointGroup: {
                id: urlChargePointSiteId,
                name: firstMember.operatorName || 'Site',
                coordinate: firstMember.coordinate,
                pin: firstMember.pin,
                address1: firstMember.address1,
                city: firstMember.city,
                operatorName: firstMember.operatorName,
                availableCount: members.filter(
                  (m) => m.chargePointState === 'available'
                ).length,
                chargePointCount: members.length,
              },
            };
            setParentGroup(siteWrapper);
          }
        })
        .catch((error) => {
          console.error('Failed to fetch parent site for deeplink:', error);
        });
    }
  }, [urlChargePointId, urlChargePointSiteId, parentGroup, setParentGroup]);

  // Fetch full charge point details when a single charge point is selected
  useEffect(() => {
    const isSingleChargePoint = selectedWrapper?.type === 'chargePoint';
    const chargePointId = selectedWrapper?.chargePoint?.id;

    if (isSingleChargePoint && chargePointId) {
      let isCancelled = false;

      setLoadingDetails(true);
      chargePointsApi
        .getChargePointById(chargePointId)
        .then((fullDetails) => {
          // Only update state if not cancelled (user hasn't closed or changed selection)
          if (isCancelled) return;

          // Update the selected charge point with full details
          if (selectedWrapper) {
            selectChargePoint({
              ...selectedWrapper,
              chargePoint: fullDetails,
            });
          }

          // If charge point belongs to a group and we don't have parent set, fetch the group
          if (fullDetails.chargePointGroupId && !parentGroup) {
            chargePointsApi
              .getChargePointGroupMembers(fullDetails.chargePointGroupId)
              .then((members) => {
                if (isCancelled) return;

                // Cache the members
                setSelectedGroupMembers(
                  members,
                  fullDetails.chargePointGroupId
                );

                // Create parent group wrapper from first member
                const firstMember = members[0];
                if (firstMember) {
                  const siteWrapper = {
                    id: `group-${fullDetails.chargePointGroupId}`,
                    type: 'chargePointGroup' as const,
                    coordinate: firstMember.coordinate,
                    pin: firstMember.pin,
                    chargePointGroup: {
                      id: fullDetails.chargePointGroupId!,
                      name: firstMember.operatorName || 'Site',
                      coordinate: firstMember.coordinate,
                      pin: firstMember.pin,
                      address1: firstMember.address1,
                      city: firstMember.city,
                      operatorName: firstMember.operatorName,
                      availableCount: members.filter(
                        (m) => m.chargePointState === 'available'
                      ).length,
                      chargePointCount: members.length,
                    },
                  };
                  setParentGroup(siteWrapper);
                }
              })
              .catch((error) => {
                console.error('Failed to fetch parent group:', error);
              });
          }
        })
        .catch((error) => {
          console.error('Failed to fetch charge point details:', error);
        })
        .finally(() => {
          if (!isCancelled) {
            setLoadingDetails(false);
          }
        });

      // Cleanup: cancel state updates if component unmounts or selection changes
      return () => {
        isCancelled = true;
      };
    }
    return undefined;
  }, [selectedWrapper?.id]); // Only trigger on wrapper ID change

  // Fetch group members when a group is selected
  useEffect(() => {
    const isGroup = selectedWrapper?.type === 'chargePointGroup';
    const groupId = selectedWrapper?.chargePointGroup?.id;

    if (isGroup && groupId) {
      let isCancelled = false;

      // Check cache first
      const cachedMembers = getCachedGroupMembers(groupId);

      if (cachedMembers) {
        // Use cached data immediately (no loading state)
        setSelectedGroupMembers(cachedMembers, groupId);

        // Fetch in background to update cache
        chargePointsApi
          .getChargePointGroupMembers(groupId)
          .then((members) => {
            if (!isCancelled) {
              setSelectedGroupMembers(members, groupId);
            }
          })
          .catch((error) => {
            console.error('Failed to refresh group members:', error);
          });
      } else {
        // No cache - show loading and fetch
        setLoadingGroup(true);
        chargePointsApi
          .getChargePointGroupMembers(groupId)
          .then((members) => {
            if (!isCancelled) {
              setSelectedGroupMembers(members, groupId);
            }
          })
          .catch((error) => {
            console.error('Failed to fetch group members:', error);
            if (!isCancelled) {
              setSelectedGroupMembers([]);
            }
          })
          .finally(() => {
            if (!isCancelled) {
              setLoadingGroup(false);
            }
          });
      }

      // Cleanup: cancel state updates if component unmounts or selection changes
      return () => {
        isCancelled = true;
      };
    } else {
      // Clear group members if not a group
      setSelectedGroupMembers([]);
    }
    return undefined;
  }, [
    selectedWrapper?.id,
    setSelectedGroupMembers,
    setLoadingGroup,
    getCachedGroupMembers,
  ]); // Only trigger on wrapper ID change

  // Map pins don't carry the site note. Storing it on the wrapper lets it travel
  // as parentGroup into the charger view, where it dedups the charger note (the
  // server falls back to the site note when a charger has none).
  const loadGroupNote = (wrapper: ChargePointWrapper) => {
    const group = wrapper.chargePointGroup!;
    return chargePointsApi
      .getChargePointSiteById(group.id)
      .then(({ note }) => {
        if (
          useChargePointsStore.getState().selectedChargePoint?.id !== wrapper.id
        )
          return;
        selectChargePoint({
          ...wrapper,
          chargePointGroup: { ...group, note: note ?? null },
        });
      })
      .catch((error) => {
        console.error('Failed to fetch group note:', error);
      });
  };

  useEffect(() => {
    if (
      selectedWrapper?.type === 'chargePointGroup' &&
      selectedWrapper.chargePointGroup?.note === undefined
    ) {
      void loadGroupNote(selectedWrapper);
    }
  }, [selectedWrapper?.id]);

  // Manual refresh function
  const handleRefresh = () => {
    const isGroup = selectedWrapper?.type === 'chargePointGroup';
    const isSingleChargePoint = selectedWrapper?.type === 'chargePoint';

    if (isGroup && selectedWrapper?.chargePointGroup?.id) {
      void loadGroupNote(selectedWrapper);
      setLoadingGroup(true);
      chargePointsApi
        .getChargePointGroupMembers(selectedWrapper.chargePointGroup.id)
        .then((members) => {
          setSelectedGroupMembers(
            members,
            selectedWrapper.chargePointGroup!.id
          );
        })
        .catch((error) => {
          console.error('Failed to refresh group members:', error);
        })
        .finally(() => {
          setLoadingGroup(false);
        });
    } else if (isSingleChargePoint && selectedWrapper?.chargePoint?.id) {
      setLoadingDetails(true);
      chargePointsApi
        .getChargePointById(selectedWrapper.chargePoint.id)
        .then((fullDetails) => {
          if (selectedWrapper) {
            selectChargePoint({
              ...selectedWrapper,
              chargePoint: fullDetails,
            });
          }
        })
        .catch((error) => {
          console.error('Failed to refresh charge point details:', error);
        })
        .finally(() => {
          setLoadingDetails(false);
        });
    }
  };

  // Back button handler for navigating to parent site
  const handleBack = () => {
    if (parentGroup) {
      selectChargePoint(parentGroup);
      setParentGroup(null);
    }
  };

  if (!selectedWrapper) {
    return null;
  }

  // Determine if it's a single charge point or a group
  const isGroup = selectedWrapper.type === 'chargePointGroup';
  const chargePoint = selectedWrapper.chargePoint;
  const chargePointGroup = selectedWrapper.chargePointGroup;

  // For groups, use the first member for details if available, otherwise use group data
  const displayChargePoint =
    isGroup && selectedGroupMembers.length > 0
      ? selectedGroupMembers[0]
      : chargePoint;

  // Get the display name
  const address =
    chargePointGroup?.address1 ||
    (selectedGroupMembers.length > 0
      ? selectedGroupMembers[0]?.address1
      : undefined);
  const displayName = isGroup
    ? uiMode === 'operator' && address
      ? t('chargePoint.operatorMode.groupTitle', { address })
      : chargePointGroup?.name ||
        (chargePointGroup?.chargePointCount
          ? t('chargePoint.groupTitle', {
              count: chargePointGroup.chargePointCount,
            })
          : t('chargePoint.detailsTitle'))
    : chargePoint?.name ||
      chargePoint?.address1 ||
      chargePoint?.operatorName ||
      t('chargePoint.detailsTitle');

  // Site availability: same counts as the pin and nearby list (the members
  // endpoint can return a subset), falling back to member states
  const siteTotal = Math.max(
    chargePointGroup?.chargePointCount || selectedGroupMembers.length,
    chargePointGroup?.availableCount ?? 0
  );
  const siteAvailable =
    chargePointGroup?.availableCount ??
    selectedGroupMembers.filter((m) => m.chargePointState === 'available')
      .length;

  // Show skeleton loader when loading new data (replaces old content)
  const isLoading = isLoadingDetails || isLoadingGroup;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-5 pt-4 pb-4 border-b border-ink/5 flex-shrink-0">
        <div className="flex items-start justify-between">
          {/* Back button (only for charge points with parent site) */}
          {!isGroup && parentGroup && (
            <button
              onClick={handleBack}
              className="p-2 -ml-2 rounded-full hover:bg-ink/5 transition-colors flex-shrink-0"
              aria-label={t('common.back')}
            >
              <svg
                className="w-5 h-5 text-muted"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
          )}

          <div className="flex-1">
            {/* Show ID for both single charge points and groups */}
            {!isGroup && chargePoint && (
              <div className="text-[11px] font-mono uppercase tracking-wide text-muted/70 mb-1">
                {t('chargePoint.id')}: {chargePoint.id}
              </div>
            )}
            {isGroup && chargePointGroup && (
              <div className="text-[11px] font-mono uppercase tracking-wide text-muted/70 mb-1">
                {t('chargePoint.siteId')}: {chargePointGroup.id}
              </div>
            )}
            <h2 className="font-heading font-semibold text-2xl leading-tight tracking-[-0.03em] text-ink">
              {displayName}
            </h2>
            {/* Only show type/power/connectors for single charge points in header */}
            {!isGroup && displayChargePoint && !isLoading && (
              <>
                <div className="flex items-center gap-1.5 flex-wrap mt-2.5">
                  <span className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-ink text-surface text-xs font-semibold tabular-nums">
                    <svg
                      className="w-3.5 h-3.5"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden
                    >
                      <path d="M13 2 4.5 13.5H11L10 22l8.5-11.5H12L13 2z" />
                    </svg>
                    {displayChargePoint.maxKwLabel}
                  </span>
                  <span className="inline-flex items-center h-7 px-2.5 rounded-full bg-ink/[0.03] text-ink/80 text-xs font-medium ring-1 ring-inset ring-ink/5">
                    {displayChargePoint.typeLabel}
                  </span>
                  {/* Connectors */}
                  {displayChargePoint.connectors &&
                    displayChargePoint.connectors.length > 0 && (
                      <>
                        {displayChargePoint.connectors.map(
                          (connector: {
                            id: number;
                            name: string;
                            iconUrl?: string;
                          }) => (
                            <span
                              key={connector.id}
                              className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full bg-ink/[0.03] text-ink/80 text-xs font-medium ring-1 ring-inset ring-ink/5"
                            >
                              {connector.iconUrl && (
                                <img
                                  src={connector.iconUrl}
                                  alt={connector.name}
                                  className="w-4 h-4 object-contain"
                                />
                              )}
                              {connector.name}
                            </span>
                          )
                        )}
                      </>
                    )}
                </div>
                {/* Site name link - show if charge point has a parent site */}
                {parentGroup && parentGroup.chargePointGroup && (
                  <button
                    onClick={handleBack}
                    className="flex items-center gap-1 mt-2 text-xs text-muted hover:text-ink transition-colors"
                  >
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                    <span className="underline">
                      {parentGroup.chargePointGroup.name}
                    </span>
                  </button>
                )}
              </>
            )}
          </div>
          {/* Refresh and Close buttons - hidden on mobile (bottom sheet has its own close button) */}
          <div className="hidden md:flex items-center gap-1 -mt-1 -mr-1">
            {/* Refresh button */}
            <button
              onClick={handleRefresh}
              disabled={isLoadingDetails || isLoadingGroup}
              className="p-2 rounded-full hover:bg-ink/5 transition-colors disabled:opacity-50 flex-shrink-0"
              aria-label={t('common.refresh')}
            >
              <svg
                className={`w-5 h-5 text-muted ${isLoadingDetails || isLoadingGroup ? 'animate-spin' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
            {/* Close button */}
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-ink/5 transition-colors flex-shrink-0"
              aria-label={t('common.close')}
            >
              <svg
                className="w-5 h-5 text-muted"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Overlap Navigation - shows when multiple markers at same location */}
      <OverlapNavigator />

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
        {isLoading ? (
          /* Skeleton Loading State */
          <ChargePointSkeleton />
        ) : (
          <>
            {/* Location note — group view (before a charger is selected) */}
            {isGroup && <OperatorNote note={chargePointGroup?.note} />}

            {/* Charger note — deduped against the location note already shown */}
            {!isGroup &&
              displayChargePoint?.note &&
              displayChargePoint.note !==
                parentGroup?.chargePointGroup?.note && (
                <OperatorNote note={displayChargePoint.note} />
              )}

            {/* Availability - only for single charge points */}
            {!isGroup && (
              <AvailabilitySection
                chargePoint={chargePoint}
                chargePointGroup={chargePointGroup}
                groupMembers={selectedGroupMembers}
                isLoadingGroup={isLoadingGroup}
              />
            )}

            {/* Passive Charge Point Notice */}
            {!isGroup && chargePoint?.chargePointState === 'passive' && (
              <div className="flex items-start gap-3 p-3 bg-monta-blue/10 border border-monta-blue/20 rounded-lg">
                <svg
                  className="w-5 h-5 text-monta-blue flex-shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div className="flex-1">
                  <p className="text-sm font-medium text-ink">
                    {t('chargePoint.passiveNotice.title')}
                  </p>
                  <p className="text-sm text-muted mt-1">
                    {t('chargePoint.passiveNotice.description')}
                  </p>
                </div>
              </div>
            )}

            {/* Site headline: free right now */}
            {isGroup && siteTotal > 0 && (
              <AvailabilityHero available={siteAvailable} total={siteTotal} />
            )}

            {/* Address & Location */}
            {(displayChargePoint || chargePointGroup) && (
              <AddressSection
                chargePoint={displayChargePoint}
                chargePointGroup={chargePointGroup}
              />
            )}

            {/* For Groups: Show power-grouped summary */}
            {isGroup && selectedGroupMembers.length > 0 && (
              <PowerGroupedSummary chargePoints={selectedGroupMembers} />
            )}

            {/* Show detailed sections when we have charge point data */}
            {displayChargePoint && (
              <>
                {/* For single charge points only */}
                {!isGroup && (
                  <>
                    {/* Pricing */}
                    <PricingSection
                      chargePoint={displayChargePoint}
                      isLoading={isLoadingDetails}
                    />

                    {/* Additional Fees */}
                    <FeesSection chargePoint={displayChargePoint} />
                  </>
                )}
              </>
            )}

            {/* For Groups: Show list of all charge points (connectors shown inline) */}
            {isGroup && selectedGroupMembers.length > 0 && (
              <>
                <div className="border-t border-ink/5 -mx-5"></div>
                <h4 className="font-heading font-semibold text-sm text-ink mt-4">
                  {uiMode === 'operator' && address
                    ? t('chargePoint.operatorMode.allChargePoints', { address })
                    : t('chargePoint.allChargePoints')}
                </h4>
                <ChargePointsList chargePoints={selectedGroupMembers} />
              </>
            )}
          </>
        )}
      </div>

      {/* Actions pinned below the scroll area so "Start charging" is always
          reachable - single charge points only */}
      {!isLoading && !isGroup && displayChargePoint && (
        <div className="flex-shrink-0 border-t border-ink/5 bg-surface px-5 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <ActionButtons
            chargePoint={displayChargePoint}
            chargePointGroup={chargePointGroup}
          />
        </div>
      )}
    </div>
  );
}
